// Sample data for local development and Deploy Previews: backend.js uses this
// instead of bookingApi.js when GATSBY_BOOKING_API is not set. ?mockSlots=0
// shows the state with no times published.
const DAY = 24 * 60 * 60 * 1000

export const STAGE = {
  ANONYMOUS: "anonymous",
  DETAILS_ONLY: "details_only",
  CODE_SENT: "code_sent",
  AWAITING_DEPOSIT: "awaiting_deposit",
  PHOTOS_RECEIVED: "photos_received",
  CONFIRMED: "confirmed",
  SENT_TO_PAID_CONSULT: "sent_to_paid_consult",
}

const MOCK_OFFSETS = {
  introCall: [
    { days: 2, hour: 14, minute: 0 },
    { days: 2, hour: 16, minute: 30 },
    { days: 4, hour: 11, minute: 0 },
  ],
  surgicalConsult: [
    { days: 3, hour: 9, minute: 30 },
    { days: 6, hour: 13, minute: 0 },
    { days: 9, hour: 15, minute: 30 },
  ],
  nonSurgicalIntake: [
    { days: 1, hour: 10, minute: 0 },
    { days: 5, hour: 12, minute: 30 },
    { days: 8, hour: 17, minute: 0 },
  ],
}

// One lead per visitor, not per click. The visitor id lives in localStorage and
// every answer is appended to that one record, so someone toggling the budget
// answer to see what happens is counted, not saved as several leads. Answers
// given before the details join the same record once the details arrive.
const VISITOR_KEY = "fi-booking-visitor"
const leads = {}

const leadFor = (visitorId) => {
  if (!leads[visitorId]) {
    leads[visitorId] = {
      leadId: `mock-lead-${visitorId}`,
      stage: STAGE.ANONYMOUS,
      budgetYes: 0,
      budgetNo: 0,
      events: [],
    }
  }
  return leads[visitorId]
}

const summary = (lead) =>
  JSON.stringify({ ...lead, events: lead.events.length })

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const forcedEmpty = () => {
  if (typeof window === "undefined") return false
  try {
    return new URLSearchParams(window.location.search).get("mockSlots") === "0"
  } catch (e) {
    return false
  }
}

export const getVisitorId = () => {
  const fresh = `v-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`
  try {
    const stored = window.localStorage.getItem(VISITOR_KEY)
    if (stored) return stored
    window.localStorage.setItem(VISITOR_KEY, fresh)
  } catch (e) {
    // Blocked storage: this visit is still one record, just not joined up
    // with a later one.
  }
  return fresh
}

export const recordEvent = async (visitorId, type, data = {}) => {
  const lead = leadFor(visitorId)
  lead.events.push({ type, at: new Date().toISOString(), ...data })
  if (type === "budget") {
    if (data.answer === "yes") lead.budgetYes += 1
    if (data.answer === "no") lead.budgetNo += 1
    lead.lastBudgetAnswer = data.answer
  }
  // eslint-disable-next-line no-console
  console.log(`[mock] ${type} ${JSON.stringify(data)} -> ${summary(lead)}`)
  return { ok: true }
}

export const createLead = async (visitorId, details) => {
  await delay(350)
  const lead = leadFor(visitorId)
  Object.assign(lead, details)
  // eslint-disable-next-line no-console
  console.log(`[mock] createLead -> ${summary(lead)}`)
  return { ok: true, leadId: lead.leadId }
}

export const updateLead = async (leadId, patch) => {
  await delay(200)
  const lead = Object.values(leads).find((l) => l.leadId === leadId)
  if (lead) Object.assign(lead, patch)
  // eslint-disable-next-line no-console
  console.log(
    `[mock] updateLead -> ${summary(lead || { leadId, ...patch, events: [] })}`
  )
  return { ok: true }
}

const AMSTERDAM = {
  locationUuid: "113ae4fa-7c54-11e9-881b-0a599d16f52a",
  location: "Amsterdam, Frederiksplein 47B",
}
const BERGEN = {
  locationUuid: "69e84f5d-0a42-11ea-9173-06f92916377c",
  location: "Bergen, Dokter van Peltlaan 5",
}
const VIRTUAL = {
  locationUuid: "2133fade-6404-11e9-881b-0a599d16f52a",
  location: null,
}

// In person, the mock alternates Amsterdam and Bergen and puts both on one
// day, so the per-location headings can be seen without real data.
const placeFor = (route, consultMode, index) => {
  if (route === "introCall") return VIRTUAL
  if (route === "surgicalConsult" && consultMode !== "clinic") return VIRTUAL
  return index % 3 === 1 ? BERGEN : AMSTERDAM
}

const DOCTOR_FOR = {
  introCall: 1452,
  surgicalConsult: 1125,
  nonSurgicalIntake: 1452,
}

// ?mockIntro=0 makes Dr. Schmidt fully booked for the coming month, which
// sends a facelift lead to Dr. Faris's consultation, as the server does.
const introBooked = () => {
  if (typeof window === "undefined") return false
  try {
    return new URLSearchParams(window.location.search).get("mockIntro") === "0"
  } catch (e) {
    return false
  }
}

export const fetchSlots = async (
  requested = "introCall",
  consultMode,
  firstStep
) => {
  await delay(450)
  const noSchmidtCalls = requested === "introCall" ? introBooked() : undefined
  const route =
    requested === "introCall" && (noSchmidtCalls || firstStep === "faris")
      ? "surgicalConsult"
      : requested
  if (forcedEmpty()) return { route, slots: [], noSchmidtCalls }
  const now = Date.now()
  const offsets = [
    ...(MOCK_OFFSETS[route] || []),
    ...(route === "nonSurgicalIntake" || consultMode === "clinic"
      ? [{ days: 5, hour: 15, minute: 0 }]
      : []),
  ]
  const slots = offsets.map(({ days, hour, minute }, i) => {
    const start = new Date(now + days * DAY)
    start.setHours(hour, minute, 0, 0)
    const place = placeFor(route, consultMode, i)
    return {
      id: `${start.toISOString()}|${place.locationUuid}`,
      start: start.toISOString(),
      userId: DOCTOR_FOR[route],
      ...place,
    }
  })
  return { route, slots, noSchmidtCalls }
}

// Mirrors ClinicMinds' own sequence: request-appointment holds the slot and
// sends the SMS, verify-appointment confirms it and returns the deposit link
// when the service has one. The code is resendable once, after 60 seconds, and
// change-mobile-number fixes a mistyped number and frees another resend.
// In the mock any six digits work except 000000, which tests the error state.
const DEPOSIT = { surgicalConsult: 30, nonSurgicalIntake: 30 }

export const requestAppointment = async (leadId, booking) => {
  await delay(600)
  const lead = Object.values(leads).find((l) => l.leadId === leadId)
  if (lead) Object.assign(lead, { booking, stage: STAGE.CODE_SENT })
  // eslint-disable-next-line no-console
  console.log(`[mock] requestAppointment ${JSON.stringify(booking)}`)
  return { ok: true, requestId: `mock-request-${Date.now()}` }
}

export const resendCode = async (requestId) => {
  await delay(400)
  // eslint-disable-next-line no-console
  console.log(`[mock] resendCode ${requestId}`)
  return { ok: true }
}

export const changeMobileNumber = async (requestId, mobile) => {
  await delay(400)
  // eslint-disable-next-line no-console
  console.log(`[mock] changeMobileNumber ${requestId} ${mobile}`)
  return { ok: true }
}

export const verifyAppointment = async (requestId, code, route) => {
  await delay(500)
  if (code === "000000") return { ok: false, error: "invalid_code" }
  const amount = DEPOSIT[route]
  // eslint-disable-next-line no-console
  console.log(
    `[mock] verifyAppointment ${requestId} -> deposit ${amount || "none"}`
  )
  return {
    ok: true,
    prepaymentRequired: Boolean(amount),
    prepaymentAmount: amount || null,
    prepaymentUrl: amount ? "https://example.com/mock-deposit-payment" : null,
  }
}

export const uploadPhotos = async (leadId, files) => {
  await delay(900)
  // eslint-disable-next-line no-console
  console.log("[mock] uploadPhotos", {
    leadId,
    files: Object.entries(files).map(([k, f]) => [k, f && f.name]),
  })
  return { ok: true }
}
